import { Injectable } from '@angular/core';
import { MarketDetectionUtil } from '~shared/utils/market-detection.util';
import {
  COUNTRY_BASED_VAT_ID_PREFIX,
  CountryCode,
  DEFAULT_COUNTRIES_PER_MARKET,
} from '~shared/constants/country-code.enum';
import { VATNumberValidationModel } from '~app/modules/authorization/models/vat.number.model';
import { VAT_ITALY_PATTERN, VAT_PORTUGAL_PATTERN } from '~shared/validators/pattern-validators';
import {
  VAT_ID_CHECK_CONFIG_FRANCE,
  VAT_ID_CHECK_CONFIG_ITALY,
  VAT_ID_CHECK_CONFIG_NETHERLANDS,
  VAT_ID_CHECK_CONFIG_PORTUGAL,
} from '~app/modules/authorization/constants/vat-id-check.constant';
import { StringUtil } from '~shared/utils/string.util';
import { TierTwoRegistrationData } from '~app/modules/authorization/models/credentials.model';
import { IBranchDataForTierTwo } from '~app/modules/authorization/models/branch.model';
import { CompanyDetailsFromIdService } from '~app/modules/authorization/services/company-details-from-id.service';
import { filter } from 'rxjs/operators';
import { FF_GET_COMPANY_FROM_KVK } from '~app/modules/authorization/constants/authorization.constant';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { combineLatest } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ConfigureCountryWiseFieldsService {
  selectedBranchData: IBranchDataForTierTwo;

  constructor(
    private companyDetailsFromIdService: CompanyDetailsFromIdService,
    private activeFeaturesConfigCatService: ActiveFeaturesConfigcatService
  ) {
    combineLatest([
      this.activeFeaturesConfigCatService.isFeatureEnabledReadyForCurrentCountry$(FF_GET_COMPANY_FROM_KVK),
      this.companyDetailsFromIdService.selectedBranchData$,
    ])
      .pipe(filter(([isEnabled, branchData]) => isEnabled && !!branchData))
      .subscribe(([, branchData]: [boolean, IBranchDataForTierTwo]) => {
        this.selectedBranchData = branchData;
      });
  }

  createTierTwoPayload(formData): TierTwoRegistrationData {
    let countryWiseData = null;

    const primaryData: TierTwoRegistrationData = {
      agreedToTermsAndConditions: true,
      businessName: formData?.businessName,
      firstName: formData?.firstName,
      lastName: formData?.lastName,
      legalAddressBuildingNumber: formData?.legalAddressBuildingNumber,
      legalAddressCity: formData?.legalAddressCity,
      legalAddressPostalCode: formData?.legalAddressPostalCode,
      legalAddressStreet: formData?.legalAddressStreet,
      legalAddressCountry: DEFAULT_COUNTRIES_PER_MARKET[MarketDetectionUtil.getCountryCode()],
    };

    if (MarketDetectionUtil.isSpanishMarket()) {
      countryWiseData = {
        taxNumber: formData?.taxNumber,
      };
    } else if (MarketDetectionUtil.isItalianMarket()) {
      countryWiseData = {
        vatNumber: formData?.vatNumber,
        legalAddressProvince: formData?.legalAddressProvince,
        legalElectronicInvoiceId: formData?.legalElectronicInvoiceId,
        legalEmailAddress: formData?.legalEmailAddress,
      };
    } else if (MarketDetectionUtil.isPortugueseMarket()) {
      countryWiseData = {
        vatNumber: formData?.vatNumber,
      };
    } else if (MarketDetectionUtil.isDutchMarket()) {
      countryWiseData = {
        legalAddressPostalCode: StringUtil.trimMultipleSpaces(formData?.legalAddressPostalCode),
        branchNumber: this.selectedBranchData?.branchNumber,
        kvkNumber: formData?.kvkNumber,
        vatNumber: formData?.vatNumber?.trim(),
      };
    } else if (MarketDetectionUtil.isFrenchMarket()) {
      countryWiseData = {
        legalAddressPostalCode: StringUtil.trimMultipleSpaces(formData?.legalAddressPostalCode),
        branchNumber: this.selectedBranchData?.branchNumber,
        taxNumber: formData?.sirenNumber, // SIREN == taxNumber
        siret: this.selectedBranchData?.siret,
      };
    }

    return {
      ...primaryData,
      ...countryWiseData,
    };
  }

  getVATPatternFromCountry(): RegExp {
    switch (MarketDetectionUtil.getCountryCode()) {
      case CountryCode.Italy:
        return VAT_ITALY_PATTERN;

      case CountryCode.Portugal:
        return VAT_PORTUGAL_PATTERN;
    }

    return VAT_ITALY_PATTERN;
  }

  createVATIdCheckConfig(): VATNumberValidationModel {
    switch (MarketDetectionUtil.getCountryCode()) {
      case CountryCode.Portugal:
        return {
          formConfig: VAT_ID_CHECK_CONFIG_PORTUGAL,
          vatPrefix: COUNTRY_BASED_VAT_ID_PREFIX[CountryCode.Portugal],
          isRedirectFromKVK: false,
        };

      case CountryCode.Netherlands:
        return {
          formConfig: VAT_ID_CHECK_CONFIG_NETHERLANDS,
          vatPrefix: '',
          isRedirectFromKVK: true,
        };

      case CountryCode.France:
        return {
          formConfig: VAT_ID_CHECK_CONFIG_FRANCE,
          vatPrefix: '',
          isRedirectFromKVK: true,
        };
    }

    return {
      formConfig: VAT_ID_CHECK_CONFIG_ITALY,
      vatPrefix: COUNTRY_BASED_VAT_ID_PREFIX[CountryCode.Italy],
      isRedirectFromKVK: false,
    };
  }
}
